import { Injectable } from '@angular/core';
import { AlertController } from 'ionic-angular';
import { APP_CONFIG } from '../../environment';
import { HttpProvider } from '../http/http';
import { CustomerProvider } from '../customer/customer';
import { RouteProvider } from '../route/route';
import { LoggerProvider } from '../logger/logger';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable()
export class MarketingProvider {  
  initialized: boolean = false;
  initPromise: Promise<any>;
  initResolve;
  lastPayload;
  marketings;

  public deviceId: string;
  
  constructor(private alertCtrl: AlertController,
              private customerProvider: CustomerProvider,
              private routeProvider: RouteProvider,
              private sanitizer: DomSanitizer,
              private httpProvider: HttpProvider) {
  }

  startMarketing() {
    LoggerProvider.callbacks.push((source, level, event, message, details) => {
      this.checkTriggerEvent(source, level, event, message, details);
      return true;
    });
    this.routeProvider.callbacks.push((enter, page, navinfo, params) => {
      return this.checkTriggerPage(enter, page, navinfo, params);
    });
    this.initialize();
  }

  async initialize(forceReload: boolean = false) {      
    if (!APP_CONFIG.phpApiBaseUrl)
      return;

    let payload: any = this.getCriteriaPayload();
    if (!payload) {
      if (!this.initPromise)
        this.initPromise = new Promise((resolve) => { this.initResolve = resolve; });
      return;
    }
    const payloadstr = JSON.stringify(payload);

    if (payloadstr === this.lastPayload) {
      if (this.initialized && !forceReload)
        return;
      else if (this.initPromise)
        return;
    }

    if (!this.initPromise)
      this.initPromise = new Promise((resolve) => { this.initResolve = resolve; });  
    this.marketings = null;
    this.lastPayload = payloadstr;
    try {
      payload = { ...payload, ...this.getExtraPayload() };
      await this.httpProvider.postExtern(`${APP_CONFIG.phpApiBaseUrl}marketing.php`, payload, {}, 'json', (res) => {
        this.initMarketings(payloadstr, res);
        return res;
      });
    }
    catch (ex) {
      LoggerProvider.trace(this, 'Failed to get marketings in marketing provider', ex);
    }
  }

  async initMarketings(payloadstr, res) {
    let customer = null;
    if (HttpProvider.hasAuthCustomerId() && this.customerProvider.hasCustomer())
      customer = await this.customerProvider.getCustomer();
    if (payloadstr === this.lastPayload) {      
      this.marketings = res.marketings;
      if (this.marketings) {
        for (let i=0; i < this.marketings.length; i++) {
          const marketing = this.marketings[i];
          for (const field in customer) {
            if (marketing.title)
              marketing.title = marketing.title.replace('{{ customer.' + field + ' }}', customer[field]);
            if (marketing.content)
              marketing.content  = marketing.content.replace('{{ customer.' + field + ' }}', customer[field]);
          }
          if (marketing.title)
            marketing.title = this.sanitizer.bypassSecurityTrustHtml(marketing.title);
          if (marketing.text)
            marketing.text = this.sanitizer.bypassSecurityTrustHtml(marketing.text);
          if (marketing.image)
            marketing.image = this.sanitizer.bypassSecurityTrustUrl(marketing.image);
        }
      }
      this.initialized = true;
      this.initResolve();
      this.initPromise = null;
    }
  }

  ensureInitialized() {
    this.initialize();
    if (this.initPromise)
      return this.initPromise;
    return null;
  }

  async getAllMarketingsForPage(page) {
    await this.ensureInitialized();
    if (!this.marketings)
      return null;
    const cards = this.marketings.filter((marketing) => marketing.event_trigger_id === '1' && marketing.event_trigger_value === page);
    return cards;
  }

  async getCardsForPage(page): Promise<any> {
    await this.ensureInitialized();
    if (!this.marketings)
      return null;
    const cards = this.marketings.filter((marketing) => marketing.target_id === '1' && marketing.event_trigger_id === '1' && marketing.event_trigger_value === page);
    return cards;
  }

  async getTopForPage(page): Promise<any> {
    await this.ensureInitialized();
    if (!this.marketings)
      return null;
    const cards = this.marketings.filter((marketing) => marketing.target_id === '2' && marketing.event_trigger_id === '1' && marketing.event_trigger_value === page);
    return cards;
  }

  async getBottomForPage(page): Promise<any> {
    await this.ensureInitialized();
    if (!this.marketings)
      return null;
    const cards = this.marketings.filter((marketing) => marketing.target_id === '3' && marketing.event_trigger_id === '1' && marketing.event_trigger_value === page);
    return cards;
  }

  getCriteriaPayload() {
    const user = this.httpProvider.getAuthEmail();
    if (!user && !LoggerProvider.uuid)
      return null;

    return {
      user: user,
      deviceid: LoggerProvider.uuid,
      region: APP_CONFIG.region,
      language: this.customerProvider.getCurrentLanguage()
    };
  }

  getExtraPayload() {
    return {
      sessionId: LoggerProvider.sessionId,
      parentSessionId: LoggerProvider.parentSessionId,
      appInfo: LoggerProvider.appInfo,
      appVersion: LoggerProvider.appVersion
    }
  }

  processButton(marketing, buttonIndex) {
    if (!APP_CONFIG.phpApiBaseUrl)
      return;

    const button = marketing.buttons[buttonIndex];
    const payload = {
      ...this.getCriteriaPayload(),
      ...this.getExtraPayload(),
      marketing: marketing,
      button: button
    }
    this.httpProvider.postExtern(`${APP_CONFIG.phpApiBaseUrl}marketing_event.php`, payload, {}, 'json');
    
    if (button.action_id === '1') { // Close
    }
    else if (button.action_id === '2') { // Goto page
      if (this.routeProvider.pages[button.action_value])
        this.routeProvider.push(this.routeProvider.pages[button.action_value], { marketing: marketing });
      else
        this.routeProvider.push({ page: button.action_value }, { marketing: marketing });
    }
    else if (button.action_id === '3') { // Goto Href
      this.routeProvider.openModal(this.routeProvider.pages.HrefPage, { header: button.text, href: button.action_value });
    }
    else if (button.action_id === '4') { // Send request
      this.httpProvider.post(button.action_value.url, button.action_value.data);
    }
    else if (button.action_id === '5') { // Replace page
      if (this.routeProvider.pages[button.action_value])
        this.routeProvider.replace(this.routeProvider.pages[button.action_value], { marketing: marketing });
      else
        this.routeProvider.replace({ page: button.action_value }, { marketing: marketing });
    }
    else if (button.action_id === '6') { // Replace Href
      this.routeProvider.replace(this.routeProvider.pages.HrefPage, { header: button.text, href: button.action_value });
    }
    marketing.done = true;
  }

  checkTriggerEvent(source, level, event, message, details) {
    if (this.marketings) {
      for (let i=0; i < this.marketings.length; i++) {
        const marketing = this.marketings[i];
        if (marketing.event_trigger_id === '5' && marketing.event_trigger_value === event) {
          marketing['metadata'] = {
            source: source,
            level: level,
            event: event,
            message: message,
            details: details
          };  
          this.triggerMarketing(marketing);
        }
      }
    }
  }

  checkTriggerPage(enter, page, navinfo, params) {
    if (this.marketings) {
      for (let i=0; i < this.marketings.length; i++) {
        const marketing = this.marketings[i];
        if (marketing.event_trigger_value === page) {
          if ((marketing.event_trigger_id === '2' && enter) || (marketing.event_trigger_id === '3' && !enter)) {
            marketing['metadata'] = {
              navinfo: navinfo
            };
            if (enter && params && params.marketing) // otherwise the marketing page could be triggered twice i.e. register offer intercepts CheckPricesPage but also goes to it
              return true;
            return this.triggerMarketing(marketing);
          }
        }
      }    
    }
    return true;
  }

  triggerMarketing(marketing) {
    // target_id 1 to 3 are handled in RmtContentComponent
    if (marketing.target_id === '4') { // Messagebox
      const buttons = [];
      for (let i=0; i < marketing.buttons; i++) {
        const button = marketing.buttons[i];
        buttons.push({
          text: button.text,
          handler: () => {
            this.processButton(marketing, i);
          }
        });
      }      
      const msgbox = this.alertCtrl.create({
        title: marketing.title,
        message: marketing.text,
        buttons: buttons
      });
      msgbox.setMode('ios');
      msgbox.present();
    }
    else if (marketing.target_id === '5') { // Page
      if (this.routeProvider.pages[marketing.title])
        this.routeProvider.push(this.routeProvider.pages[marketing.title], { marketing: marketing });
      else
        this.routeProvider.push({ page: marketing.title }, { marketing: marketing });
      return false; // if RouteProvider is used return false, so not the other page will be shown
    }
    else if (marketing.target_id === '6') { // Analytics event

    }
    else if (marketing.target_id === '7') { // Marketing page
      this.routeProvider.push(this.routeProvider.pages.MarketingPage, { marketing: marketing });
      return false; // if RouteProvider is used return false, so not the other page will be shown
    }
    return true;
  }
}



// WEBPACK FOOTER //
// ./src/providers/marketing/marketing.ts